/**
 * Coach Profile — Builder
 *
 * Takes a snapshot of observations (across ALL scopes the trader has —
 * setups, ATMs, day, etc.) and reduces them into a single durable
 * CoachProfile. The profile is the trader's CURRENT state-of-edge:
 *
 *   - edges          — success-severity observations sorted by impact
 *   - leaks          — warning/critical observations sorted by impact
 *   - themes         — top-line headline pulled from each cluster
 *   - signatures     — stable identity strings used for drift comparison
 *
 * Why this layer exists:
 *   Without persistence, every Coach run starts cold. The trader sees the
 *   same observation cards, but Coach itself has no memory of whether a
 *   pattern is new today, has been firing for 4 weeks, or finally went away.
 *   The profile is the foundation for that memory — diffProfile reads two
 *   profiles to find what changed; the dashboard renders the current view.
 *
 * Pure: takes observations + meta in, returns a profile out. No I/O.
 * Storage and orchestration live in storeProfile.js and useCoachProfile.
 */

const SUCCESS = 'success';
const WARNING = 'warning';
const CRITICAL = 'critical';

export const PROFILE_SCHEMA_VERSION = 1;

// Synthesis observations are produced by the synthesis layer and are NOT
// individual signals — they're meta-summaries. The profile should be built
// from the underlying analyzer observations, not the meta-summary, otherwise
// drift comparisons would be dominated by the synthesis card flipping
// between scopes.
const SYNTHESIS_ANALYZER_KEY = 'synthesis';

/**
 * Stable signature for an observation — used as the cross-snapshot identity
 * key. Two observations with the same signature in different snapshots are
 * the "same finding" for drift purposes (same scope + same scopeId + same
 * analyzer + same hint suffix from the id).
 *
 * Must NOT include any timestamps or evidence values that change every run.
 *
 * Examples:
 *   "setup:reversal:emaProximityIntelligence:pullback-edge:21"
 *   "atm:BE-then-trail:atmBracketCalibration:stop-tight-vs-mae"
 *   "day:2026-05-14:dayMistakes:fomo"
 */
export const signatureOf = (obs) => {
  if (!obs) return '';
  const scope = obs.scope || 'global';
  const scopeId = obs.scopeId || '';
  // The id format is `${analyzerKey}:${scopeId}:${hint}` per types.js. We
  // strip the leading scopeId-or-analyzer parts to extract a stable suffix.
  // Falling back on (analyzer + id) keeps signatures unique even when an
  // analyzer doesn't follow the id convention exactly.
  const analyzer = obs.analyzer || (typeof obs.id === 'string' ? obs.id.split(':')[0] : 'unknown');
  // The id already encodes scopeId + hint; we re-prefix with scope so the
  // signature is globally unique across scopes that share an entity name.
  const idTail = typeof obs.id === 'string' ? obs.id : `${analyzer}:${scopeId}`;
  return `${scope}:${idTail}`;
};

const isWin     = (sev) => sev === SUCCESS;
const isLeak    = (sev) => sev === WARNING || sev === CRITICAL;

/**
 * Convert one observation to a profile entry. The profile entry strips
 * heavy fields (full evidence) and keeps the durable identity + the
 * presentation fields the dashboard needs.
 */
const toProfileEntry = (obs, generatedAt) => ({
  signature: signatureOf(obs),
  observationId: obs.id || '',
  analyzer: obs.analyzer || '',
  scope: obs.scope || 'global',
  scopeId: obs.scopeId || '',
  category: obs.category || '',
  severity: obs.severity || 'info',
  title: obs.title || '',
  detail: obs.detail || '',
  action: obs.action || '',
  priority: typeof obs.priority === 'number' ? obs.priority : 50,
  confidence: typeof obs.confidence === 'number' ? obs.confidence : 0.7,
  // First/last seen are filled in by the merge step; default to now so a
  // never-before-seen entry shows up as "first seen this run."
  firstSeenAt: generatedAt,
  lastSeenAt: generatedAt,
  occurrences: 1,
  // Evidence is preserved for the most-recent run only; drift code does NOT
  // compare evidence to detect drift (signature is the identity key), but
  // the dashboard wants to surface a few fields.
  evidenceSummary: summarizeEvidence(obs.evidence),
});

/**
 * Pull a few human-readable numbers out of the evidence blob so the
 * dashboard doesn't have to know how to render every analyzer's bespoke
 * shape. Extracts the most commonly useful fields if present.
 */
const summarizeEvidence = (evidence) => {
  if (!evidence || typeof evidence !== 'object') return null;
  const out = {};
  // Common WR-gap shape across many analyzers
  for (const k of ['wr', 'gap', 'delta', 'lift', 'share', 'avgCapture', 'avgMaeR', 'avgMfeR']) {
    if (typeof evidence[k] === 'number') out[k] = evidence[k];
  }
  // Counts the dashboard likes to surface
  for (const k of ['count', 'sampleSize', 'tradeCount', 'winnerCount', 'loserCount', 'overrideCount']) {
    if (typeof evidence[k] === 'number') out[k] = evidence[k];
  }
  return Object.keys(out).length > 0 ? out : null;
};

/**
 * Merge a new entry into a list of prior entries. If a prior entry with the
 * same signature exists, carry forward the firstSeenAt and increment the
 * occurrences count; otherwise insert as new.
 *
 * The "list" here is a Map keyed by signature for O(1) lookup; we convert
 * to a sorted array at the end of buildProfile.
 */
const mergeEntry = (map, entry, prior) => {
  const sig = entry.signature;
  if (prior && prior.has(sig)) {
    const p = prior.get(sig);
    map.set(sig, {
      ...entry,
      firstSeenAt: p.firstSeenAt || entry.firstSeenAt,
      occurrences: (p.occurrences || 0) + 1,
    });
  } else {
    map.set(sig, entry);
  }
};

/**
 * Build a CoachProfile from a flat list of observations gathered across
 * scopes. Optionally merge against a prior profile to carry forward
 * firstSeenAt + increment occurrences.
 *
 * @param {Object} args
 * @param {Array<Observation>} args.observations  Flat list of all observations
 * @param {Object} [args.priorProfile]            Previous profile to merge against
 * @param {string} [args.generatedAt]             ISO string; defaults to now
 * @param {number} [args.windowDays]              The lookback window the observations cover
 * @param {number} [args.tradeCount]              Trades considered (just stored on profile)
 * @returns {CoachProfile}
 */
export const buildProfile = ({
  observations = [],
  priorProfile = null,
  generatedAt,
  windowDays = 30,
  tradeCount = 0,
} = {}) => {
  const now = generatedAt || new Date().toISOString();

  // Index prior entries by signature for fast lookup during the merge.
  const priorEdgeMap = new Map();
  const priorLeakMap = new Map();
  if (priorProfile) {
    for (const e of (priorProfile.edges || [])) priorEdgeMap.set(e.signature, e);
    for (const l of (priorProfile.leaks || [])) priorLeakMap.set(l.signature, l);
  }

  const edgeMap = new Map();
  const leakMap = new Map();
  // Track every signature seen this run so we can compute resolvedLeaks.
  const seenSignatures = new Set();

  for (const obs of observations) {
    if (!obs) continue;
    if (obs.analyzer === SYNTHESIS_ANALYZER_KEY) continue;
    const entry = toProfileEntry(obs, now);
    seenSignatures.add(entry.signature);
    if (isWin(obs.severity)) {
      // If a prior LEAK with the same signature is now an EDGE, that's
      // unusual but possible (analyzer flipped severity). The new severity
      // wins; carry forward firstSeenAt from whichever side held it.
      const prior = priorEdgeMap.has(entry.signature) ? priorEdgeMap : priorLeakMap;
      mergeEntry(edgeMap, entry, prior);
    } else if (isLeak(obs.severity)) {
      const prior = priorLeakMap.has(entry.signature) ? priorLeakMap : priorEdgeMap;
      mergeEntry(leakMap, entry, prior);
    }
    // INFO observations are intentionally dropped from the profile — the
    // profile is about edges and leaks, not flavor commentary. Drift on
    // info-only observations is too noisy to be useful in a coach summary.
  }

  // Resolved leaks — anything in priorProfile.leaks that did NOT show up in
  // this run AND is not an edge now. Those are the trader's wins; surface
  // them in their own list so the dashboard can celebrate progress.
  const resolvedLeaks = [];
  for (const [sig, prior] of priorLeakMap) {
    if (seenSignatures.has(sig)) continue;
    resolvedLeaks.push({
      ...prior,
      resolvedAt: now,
      // Persistence: how long the leak ran before resolving — useful context
      // ("you fixed this after 3 weeks of it firing").
      // Note: full week-counting requires history; this is the inter-snapshot
      // approximation. A per-week count lives in the snapshot history.
    });
  }

  // Sort by priority (desc), then by recency of firstSeenAt for ties.
  const byPriorityDesc = (a, b) =>
    (b.priority || 0) - (a.priority || 0) ||
    (b.firstSeenAt || '').localeCompare(a.firstSeenAt || '');
  const edges = [...edgeMap.values()].sort(byPriorityDesc);
  const leaks = [...leakMap.values()].sort(byPriorityDesc);

  // Themes — short headline strings the dashboard pins at the top.
  const themes = {
    dominantEdge: edges[0]?.title || null,
    dominantLeak: leaks[0]?.title || null,
    edgeCount: edges.length,
    leakCount: leaks.length,
    resolvedCount: resolvedLeaks.length,
    // Most-touched scope = where the most observations are concentrated.
    // Useful for "Coach is mostly thinking about Setup-X this week."
    hottestScopeId: pickHottestScope([...edges, ...leaks]),
  };

  return {
    schemaVersion: PROFILE_SCHEMA_VERSION,
    generatedAt: now,
    windowDays,
    tradeCount,
    edges,
    leaks,
    resolvedLeaks,
    themes,
    // Compact signature snapshot used by drift code + by the next buildProfile
    // run. Keeping just signatures (not full entries) keeps history small.
    signatures: [...seenSignatures],
  };
};

/**
 * Pick the scopeId that appears most often across edges + leaks. Returns
 * null when no scope clearly dominates (or no observations exist).
 */
const pickHottestScope = (entries) => {
  if (!Array.isArray(entries) || entries.length === 0) return null;
  const freq = new Map();
  for (const e of entries) {
    const id = e.scopeId;
    if (!id) continue;
    freq.set(id, (freq.get(id) || 0) + 1);
  }
  if (freq.size === 0) return null;
  let topId = null, topCount = 0;
  for (const [id, n] of freq) {
    if (n > topCount) { topCount = n; topId = id; }
  }
  // Require at least 2 occurrences to call it "hottest" — single-mention
  // scopes shouldn't claim the spotlight.
  return topCount >= 2 ? topId : null;
};

// Exports for tests
export {
  toProfileEntry,
  summarizeEvidence,
  pickHottestScope,
  mergeEntry,
};
